import { OrderState } from "@app/store/order.slice";
import { CustomerState } from "@app/store/customer.slice";
import { ServiceState } from "@app/store/service.slice";
import { SettingState } from "@app/store/setting.slice";
import { OrderKind, OrderSummary, RoutePoint, StopSummary } from "@app/domain";
import { countStopDetailItems, isStopDetailsHidden } from "@app/shared/utils/stopDetailsCount";
import { resolveCustomFields } from "./resolveCustomFields";
import { statusToTone } from "./statusToTone";
import { derivePickupStatus, deriveDropoffStatus } from "./stopStatus";
import { toProof } from "./toProof";

export type OrderSummarySlices = {
  order: OrderState;
  customer: CustomerState;
  service: ServiceState;
  setting: SettingState;
  isDropoffTracking: boolean;
};

export function toOrderKind(type: OrderState["type"]): OrderKind {
  switch (type) {
    case "Ondemand":
      return "ondemand";
    case "Pickup":
      return "pickup";
    case "Delivery":
      return "delivery";
    case "PickupDelivery":
    default:
      return "pickupDelivery";
  }
}

/**
 * Maps the (unchanged) Redux `order`/`customer`/`service`/`setting` slices —
 * kept live by both the initial `fetchTrackOrder` thunk and the socket.io
 * push updates (`src/hooks/socket.ts`) — onto the v2 `OrderSummary` shape.
 * This is the one place that translation happens; every `features/**` page
 * keeps reading a plain `OrderSummary`, unaware any of this exists.
 */
export function toOrderSummary({ order, customer, service, setting, isDropoffTracking }: OrderSummarySlices): OrderSummary {
  const kind = toOrderKind(order.type);
  const dropoffs = order.dropoffs ?? [];

  const route: RoutePoint[] = [
    ...(order.pickup ? [{ id: order.pickup.id, kind: "pickup" as const, lat: order.pickup.lat, lng: order.pickup.lng }] : []),
    ...dropoffs.map((dropoff) => ({ id: dropoff.id, kind: "dropoff" as const, lat: dropoff.lat, lng: dropoff.lng })),
  ];

  const pickup: StopSummary | null = order.pickup
    ? {
        id: order.pickup.id,
        kind: "pickup",
        label: "Pickup",
        address: order.pickup.address,
        status: derivePickupStatus(kind, order.status, order.stage),
        completedAt: order.pickup.deliveryDate || undefined,
        // Zero while dropoff-tracking is active: `StopDetailsPage` hides
        // Pickup's Proof and Details sections entirely in that mode (see
        // `isStopDetailsHidden`), so this badge must agree there's nothing
        // to show rather than counting items the customer can't see.
        detailsCount: isStopDetailsHidden("pickup", isDropoffTracking)
          ? 0
          : countStopDetailItems({
              addressDetail: order.pickup.addressDetail,
              customerDescription: order.pickup.customerDescription,
              note: order.pickup.proofNote,
              proofs: (order.pickup.proofs ?? []).map(toProof),
              customFields: resolveCustomFields(order.pickup.rawCustomFields, order.pickup.rawCustomFieldDefinitions),
            }),
        description: order.pickup.customerDescription,
      }
    : null;

  const dropoffSummaries: StopSummary[] = dropoffs.map((dropoff, index) => ({
    id: dropoff.id,
    kind: "dropoff",
    label: `Dropoff ${index + 1} of ${dropoffs.length}`,
    address: dropoff.address,
    status: deriveDropoffStatus(kind, dropoff.status),
    completedAt: dropoff.deliveryDate || undefined,
    detailsCount: countStopDetailItems({
      addressDetail: dropoff.addressDetail,
      customerDescription: dropoff.customerDescription,
      note: dropoff.proofNote,
      proofs: (dropoff.proofs ?? []).map(toProof),
      customFields: resolveCustomFields(dropoff.rawCustomFields, dropoff.rawCustomFieldDefinitions),
    }),
    description: dropoff.customerDescription,
  }));

  const etaAt = order.realETA === 0 ? null : order.realETA;

  return {
    id: order.orderId,
    code: order.code,
    kind,
    status: {
      code: order.status,
      label: order.statusDescription,
      tone: statusToTone(order.status),
    },
    logoUrl: setting.logoTypeUrl,
    mapProvider:
      setting.map?.type === "google"
        ? { kind: "google", apiKey: setting.map.apiKey }
        : { kind: "osm", tileUrl: setting.map?.url },
    dateFormat: setting.dateFormat,
    timeFormat: setting.timeFormat,
    etaAt,
    etaMinutes: etaAt !== null ? Math.max(0, Math.round((etaAt - Date.now()) / 60000)) : null,
    isLastDropOff: order.isLastDropOff ?? false,
    remainingQueuePosition: order.remainingQueuePosition ?? null,
    customer: {
      id: customer.customerId,
      name: customer.name,
      avatarUrl: customer.avatarPhoto,
      customFields: resolveCustomFields(customer.rawCustomFields, customer.rawCustomFieldDefinitions),
    },
    driver: order.driver
      ? {
          // The real API's `DriverType` has no `id` field at all — the
          // driver's phone number is the closest thing to a stable,
          // real (not invented) identifier available on it.
          id: order.driver.phone,
          name: order.driver.name,
          avatarUrl: order.driver.avatarPhoto,
          phone: order.driver.phone,
          rating: order.driver.rate,
          vehicle: {
            plate: order.driver.vehicle.plate,
            color: order.driver.vehicle.color,
            model: order.driver.vehicle.model,
          },
          customFields: resolveCustomFields(order.driver.customFields, order.driver.customFieldDefinitions),
        }
      : null,
    service: {
      title: service.service?.title ?? "",
      icon: service.service?.icon ?? "",
      options: service.service?.options ?? [],
    },
    vehicleType: service.vehicle
      ? {
          title: service.vehicle.title,
          icon: service.vehicle.icon,
          options: service.vehicle.options,
        }
      : null,
    customFields: resolveCustomFields(order.rawCustomFields, order.rawCustomFieldDefinitions),
    route,
    pickup,
    dropoffs: dropoffSummaries,
  };
}
